/**
 * useNotifications — React hook for the notification center.
 *
 * Spec ref: SPEC-146 §P-263 (Sprint 70, 2026-09-07) — F-001 Notificaciones
 *           In-App. ADR-043 (decisión).
 *
 * Provides:
 *   - unreadCount: number — for the sidebar badge (polls every 30s)
 *   - notifications: INotification[] — current page list
 *   - isLoading: boolean
 *   - error: string | null
 *   - markAsRead(id), markAsUnread(id), archive(id), markAllAsRead(),
 *     archiveAllRead(), deleteNotification(id) — mutation helpers
 *   - refresh() — manual refetch
 *
 * WebSocket integration:
 *   - Listens to `notification:new` event (emitted by backend when a new
 *     notification is created)
 *   - On event: increment unreadCount + show toast with action button
 *
 * Anti-§6: pure data-fetching hook — no business logic. The triggers that
 *   CREATE notifications are in the backend services (commission, auth,
 *   conference, etc.) — this hook only READS + mutates state.
 * Anti-P-041: 0 `as any` casts. Types are explicit.
 * Anti-P-042: 0 `console.*` calls.
 * Anti-P-043: AbortController for in-flight fetch cancellation on unmount.
 */

'use client';

import { useState, useEffect, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { toast } from 'sonner';
import { apiFetch, ApiClientError } from '@/lib/api-client';
import { useSocket } from '@/hooks/use-socket';

// ─── Types ───────────────────────────────────────────────────────────────────

export type NotificationCategory =
  | 'commission'
  | 'referral'
  | 'conference'
  | 'market'
  | 'market_product_pending'
  | 'market_product_approved'
  | 'market_product_rejected'
  | 'kyc'
  | 'payout'
  | 'testimonial'
  | 'dispute'
  | 'system';

export interface Notification {
  id: string;
  userId: string;
  category: NotificationCategory;
  type: string;
  title: string;
  body: string;
  data?: Record<string, unknown> | null;
  actionUrl?: string | null;
  actionLabel?: string | null;
  readAt: string | null;
  archivedAt: string | null;
  createdAt: string;
}

interface ListNotificationsResponse {
  data: Notification[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    unreadCount: number;
  };
}

interface UnreadCountResponse {
  data: { unreadCount: number };
}

// ─── Hook ────────────────────────────────────────────────────────────────────

interface UseNotificationsOptions {
  category?: NotificationCategory;
  unreadOnly?: boolean;
  archivedOnly?: boolean;
  page?: number;
  limit?: number;
  // If true, only fetch unreadCount (skip the list fetch — for sidebar badge)
  countOnly?: boolean;
}

export function useNotifications(options: UseNotificationsOptions = {}) {
  const router = useRouter();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState<number>(0);
  const [total, setTotal] = useState<number>(0);
  const [page, setPage] = useState<number>(options.page ?? 1);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Build query string from options
  const buildQuery = useCallback((opts: UseNotificationsOptions): string => {
    const params = new URLSearchParams();
    if (opts.category) params.set('category', opts.category);
    if (opts.unreadOnly) params.set('unread', 'true');
    if (opts.archivedOnly) params.set('archived', 'true');
    if (opts.page) params.set('page', String(opts.page));
    if (opts.limit) params.set('limit', String(opts.limit));
    const qs = params.toString();
    return qs ? `?${qs}` : '';
  }, []);

  // Fetch the unread count (lightweight — for the sidebar badge)
  const fetchUnreadCount = useCallback(async (signal?: AbortSignal): Promise<void> => {
    try {
      const data = await apiFetch<UnreadCountResponse>('/notifications/unread-count', { signal, rawResponse: true });
      setUnreadCount(data.data.unreadCount);
    } catch (err) {
      // Silent fail — the badge is non-critical. Log only for debugging.
      if (err instanceof ApiClientError && err.status === 401) {
        // User logged out — don't show error, just set count to 0
        setUnreadCount(0);
      }
      // Other errors (network, 500, etc.) — leave the previous count as-is
    }
  }, []);

  // Fetch the notification list
  // QA-E2E-015 fix (Sprint 80, 2026-09-10): infinite loop bug.
  // Pre-fix: `useCallback` had `options` (an object) as a dependency.
  // Every render creates a new `options` object (even if the values are
  // the same), so `fetchList` changed reference on every render, which
  // triggered the `useEffect` below (which depends on `fetchList`),
  // which called `fetchList` again, which caused a re-render, which
  // created a new `options` object, etc. → infinite fetch loop.
  // The fix: depend on the INDIVIDUAL fields of `options` (primitives)
  // instead of the `options` object itself. Primitives are compared by
  // value, so the callback only changes when a field actually changes.
  const fetchList = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      try {
        setIsLoading(true);
        setError(null);
        const query = buildQuery(options);
        const data = await apiFetch<ListNotificationsResponse>(`/notifications${query}`, { signal, rawResponse: true });
        setNotifications(data.data);
        setUnreadCount(data.pagination.unreadCount);
        setTotal(data.pagination.total);
        setPage(data.pagination.page);
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof ApiClientError) {
          setError(err.message);
        } else {
          setError('Error al cargar notificaciones');
        }
      } finally {
        setIsLoading(false);
      }
    },
    // QA-E2E-015 fix: depend on individual primitive fields instead of
    // the `options` object. This prevents the infinite re-render loop.
    [buildQuery, options.category, options.unreadOnly, options.archivedOnly, options.page, options.limit],
  );

  // Initial fetch + polling (every 30s for unreadCount — ADR-043 §5.2)
  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;

    if (options.countOnly) {
      // Sidebar badge mode — only fetch unreadCount, poll every 30s
      fetchUnreadCount(controller.signal);
      const interval = setInterval(() => {
        fetchUnreadCount();
      }, 30_000);
      return () => {
        controller.abort();
        clearInterval(interval);
      };
    }

    // Full mode — fetch list + unreadCount
    fetchList(controller.signal);
    return () => {
      controller.abort();
    };
  }, [fetchList, fetchUnreadCount, options.countOnly]);

  // WebSocket listener for `notification:new` events
  const socket = useSocket();
  useEffect(() => {
    if (!socket.isConnected) return;

    // useSocket().on() returns an unsubscribe function (cleanup pattern).
    // The handler receives all payload args as `unknown[]` (per the socket
    // API contract). For `notification:new`, the first arg is the Notification
    // payload. We cast it via runtime validation (TypeScript-safe cast — the
    // backend emits the exact shape we expect per SPEC-146 P-258).
    const unsubscribe = socket.on('notification:new', (...args: unknown[]) => {
      const payload = args[0] as Notification;
      if (!payload || typeof payload.id !== 'string') return;

      // Increment unread count
      setUnreadCount((prev) => prev + 1);

      // Show toast with action button (if actionUrl + actionLabel)
      const actionUrl = payload.actionUrl;
      const action =
        payload.actionUrl && payload.actionLabel
          ? {
              label: payload.actionLabel,
              // Anti-P-041: cast justified — `actionUrl` comes from the
              // backend (SPEC-146 P-258), which emits a relative URL string
              // like "/app/wallet" or "/app/notifications". Next.js 16
              // typed-routes requires `RouteImpl<string>` but we can't
              // statically type runtime-validated URLs. The cast is safe
              // because the backend always emits valid internal paths
              // (INV-NOT-06: actionUrl maxlength 200, internal URL only).
              onClick: () => router.push(actionUrl as never),
            }
          : undefined;

      toast.info(payload.title, {
        description: payload.body,
        action,
        duration: 6000, // 6 seconds (longer than default 4s)
      });

      // If the user is currently on the notifications page, prepend the
      // new notification to the list (no need to refetch)
      setNotifications((prev) => [payload, ...prev]);
    });

    return unsubscribe;
  }, [socket, router]);

  // ─── Mutations ─────────────────────────────────────────────────────────────

  const markAsRead = useCallback(async (id: string): Promise<void> => {
    try {
      await apiFetch('/notifications/' + encodeURIComponent(id) + '/read', { method: 'PATCH' });
      // Optimistic update — mark as read locally
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, readAt: new Date().toISOString() } : n)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      if (err instanceof ApiClientError) {
        toast.error(err.message);
      }
    }
  }, []);

  const markAsUnread = useCallback(async (id: string): Promise<void> => {
    try {
      await apiFetch('/notifications/' + encodeURIComponent(id) + '/unread', { method: 'PATCH' });
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, readAt: null } : n)),
      );
      setUnreadCount((prev) => prev + 1);
    } catch (err) {
      if (err instanceof ApiClientError) {
        toast.error(err.message);
      }
    }
  }, []);

  const archive = useCallback(async (id: string): Promise<void> => {
    try {
      await apiFetch('/notifications/' + encodeURIComponent(id) + '/archive', { method: 'PATCH' });
      // Remove from the list (archived items go to "Archivadas" tab)
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (err) {
      if (err instanceof ApiClientError) {
        toast.error(err.message);
      }
    }
  }, []);

  const markAllAsRead = useCallback(async (): Promise<void> => {
    try {
      await apiFetch('/notifications/read-all', { method: 'PATCH' });
      setNotifications((prev) =>
        prev.map((n) => ({ ...n, readAt: n.readAt ?? new Date().toISOString() })),
      );
      setUnreadCount(0);
      toast.success('Todas las notificaciones marcadas como leídas');
    } catch (err) {
      if (err instanceof ApiClientError) {
        toast.error(err.message);
      }
    }
  }, []);

  const archiveAllRead = useCallback(async (): Promise<void> => {
    try {
      await apiFetch('/notifications/archive-all', { method: 'PATCH' });
      // Remove all read notifications from the list
      setNotifications((prev) => prev.filter((n) => n.readAt === null));
      toast.success('Notificaciones leídas archivadas');
    } catch (err) {
      if (err instanceof ApiClientError) {
        toast.error(err.message);
      }
    }
  }, []);

  const deleteNotification = useCallback(async (id: string): Promise<void> => {
    try {
      await apiFetch('/notifications/' + encodeURIComponent(id), { method: 'DELETE' });
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    } catch (err) {
      if (err instanceof ApiClientError) {
        toast.error(err.message);
      }
    }
  }, []);

  const refresh = useCallback(async (): Promise<void> => {
    await fetchList();
    await fetchUnreadCount();
  }, [fetchList, fetchUnreadCount]);

  return {
    notifications,
    unreadCount,
    total,
    page,
    isLoading,
    error,
    markAsRead,
    markAsUnread,
    archive,
    markAllAsRead,
    archiveAllRead,
    deleteNotification,
    refresh,
  };
}
